import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [technician, setTechnician] = useState(null);
    const [client, setClient] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [urlParams, setUrlParams] = useState({});

    // API helper - memoized to prevent infinite re-renders
    const apiRequest = useCallback(async (url, options = {}) => {
        const defaultOptions = {
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                ...options.headers
            }
        };

        const response = await fetch(url, {
            ...defaultOptions,
            ...options
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
        }

        return response.json();
    }, []);

    useEffect(() => {
        initializeAuth();
    }, []);

    const initializeAuth = async () => {
        try {
            setLoading(true);
            setError('');

            // Check for required URL parameters (same as elevate)
            const params = new URLSearchParams(window.location.search);
            const location = params.get('location');
            const user = params.get('user');
            const token = params.get('token');

            if (!location || !user) {
                throw new Error('Missing required parameters: location and user');
            }

            console.log('🔗 Ejento authentication:', { location, user });

            setUrlParams({ location, user, token });

            // Verify authentication with backend
            const queryString = `location=${location}&user=${user}${token ? `&token=${token}` : ''}`;
            const result = await apiRequest(`/api/auth/verify-ejento?${queryString}`);

            if (result.success) {
                setTechnician(result.data.technician);
                setClient(result.data.client);
                setUserRole(result.data.userRole);
                console.log('✅ User authenticated with role:', result.data.userRole);
            } else {
                throw new Error(result.message || 'Authentication failed');
            }

        } catch (err) {
            console.error('❌ Authentication error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Update technician persona - memoized to prevent infinite re-renders
    const updatePersona = useCallback(async (persona) => {
        try {
            const response = await apiRequest(`/api/technicians/crm/${technician.crmCode}/persona`, {
                method: 'PUT',
                body: JSON.stringify({ persona })
            });

            if (response.success) {
                setTechnician(prev => ({
                    ...prev,
                    persona: response.data.technician.persona
                }));
                return response;
            }
        } catch (error) {
            console.error('Error updating persona:', error);
            throw error;
        }
    }, [apiRequest, technician]);

    // Get dashboard stats - memoized to prevent infinite re-renders
    const getDashboardStats = useCallback(async (locationFilter = null) => {
        try {
            const queryString = `${window.location.search}${locationFilter ? `&location=${locationFilter}` : ''}`;
            const response = await apiRequest(`/api/ejento/dashboard/stats${queryString}`);
            return response;
        } catch (error) {
            console.error('Error fetching dashboard stats:', error);
            throw error;
        }
    }, [apiRequest]);

    // Get conversations - memoized to prevent infinite re-renders
    const getConversations = useCallback(async (filters = {}) => {
        try {
            const queryParams = new URLSearchParams(window.location.search);
            Object.keys(filters).forEach(key => {
                if (filters[key]) queryParams.set(key, filters[key]);
            });

            const response = await apiRequest(`/api/ejento/conversations?${queryParams.toString()}`);
            return response;
        } catch (error) {
            console.error('Error fetching conversations:', error);
            throw error;
        }
    }, [apiRequest]);

    // Get appointments - memoized to prevent infinite re-renders
    const getAppointments = useCallback(async (filters = {}) => {
        try {
            const queryParams = new URLSearchParams(window.location.search);
            Object.keys(filters).forEach(key => {
                if (filters[key]) queryParams.set(key, filters[key]);
            });

            const response = await apiRequest(`/api/ejento/appointments?${queryParams.toString()}`);
            return response;
        } catch (error) {
            console.error('Error fetching appointments:', error);
            throw error;
        }
    }, [apiRequest]);

    const value = {
        technician,
        client,
        userRole,
        loading,
        error,
        urlParams,
        isAdmin: userRole === 'admin',
        isManager: userRole === 'manager' || userRole === 'admin',
        updatePersona,
        getDashboardStats,
        getConversations,
        getAppointments,
        apiRequest
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};